// client/src/components/pages/PeriodSummary/SummaryTable.tsx (converted from .js, ST-00209)
//
// The main Period Summary grid (ST-00174): one sortable row per Ref No /
// Order No, each expandable into a GroupDetail panel.
//
// Column budget (UX pass): the first cut had 14 columns and ran off the right
// edge of a 1440px screen, so everything that is really an ATTRIBUTE of the key
// (its routes, its products) moved onto a secondary line inside the Key cell and
// the freed width went to an Activity sparkline — the one thing the table could
// not answer before ("when in the period did this move?"). What is left is
// ~1100px wide, which fits a 1440px window with the sidebar open.
//
// ST-00209: each key's row starts with ROUTE_BOUNDARY_CLASS, a divider that stays visible
// in dark mode, so the eye can tell one key's row (and its mirrored sparkline) from the next.

import React, { type ComponentType } from 'react';
import {
  FaChevronDown,
  FaChevronRight,
  FaSortAmountDown,
  FaSortAmountUp,
  FaInbox,
} from 'react-icons/fa';
import GroupDetailJs from './GroupDetail';
import SparklineJs from './Sparkline';
import { ROUTE_BOUNDARY_CLASS } from './routeBoundary';
import {
  formatCount,
  formatTonnes,
  formatPct,
  formatTimestampDayShort,
  variancePctOf,
  varianceToneClass,
  routeLabel,
  groupByLabel,
  isGroupingRoute,
} from './utils';
import type {
  PeriodBreakdownCache,
  PeriodFilterRoute,
  PeriodGroupBy,
  PeriodGroupDetailProps,
  PeriodLoadBreakdown,
  PeriodQueryParams,
  PeriodSide,
  PeriodSort,
  PeriodSparklineEntry,
  PeriodSummaryGroup,
} from '../../../types/clientPages/periodSummary';

// Typed boundaries for the still-JS siblings (their inferred prop types are too loose to use).
const GroupDetail = GroupDetailJs as unknown as ComponentType<PeriodGroupDetailProps>;
const Sparkline = SparklineJs as unknown as ComponentType<{
  days?: string[];
  entry?: PeriodSparklineEntry;
  side?: PeriodSide;
  keyLabel?: string;
}>;

const MAX_PRODUCT_CHIPS = 2;

interface SummaryColumn {
  id: string;
  label: string;
  align: 'left' | 'right';
  sortable?: boolean;
  wrap?: boolean;
}

/**
 * @param keyLabel      "Ref No" / "Order No"
 * @param showBothSides false once a single ticket type is selected, in which case
 *   dispatch / receipt / variance are all either zero or meaningless
 */
const columns = (keyLabel: string, showBothSides: boolean): SummaryColumn[] =>
  (
    [
      { id: 'key', label: keyLabel, align: 'left' },
      { id: 'tickets', label: 'Tickets', align: 'right' },
      { id: 'nettTonnes', label: 'Nett t', align: 'right' },
      showBothSides && { id: 'dispatchTonnes', label: 'Dispatch t', align: 'right' },
      showBothSides && { id: 'receiptTonnes', label: 'Receipt t', align: 'right' },
      showBothSides && { id: 'varianceTonnes', label: 'Variance t', align: 'right' },
      { id: 'activity', label: 'Activity', align: 'left', sortable: false },
      { id: 'firstTicket', label: 'Period', align: 'left' },
      { id: 'hauliers', label: 'Hauliers · vehicles', align: 'right', wrap: true },
      { id: 'amendedTickets', label: 'Amended', align: 'right' },
    ] as Array<SummaryColumn | false>
  ).filter((col): col is SummaryColumn => Boolean(col));

const ProductChips = ({ products = [] }: { products?: string[] }) => {
  if (products.length === 0) return null;
  const shown = products.slice(0, MAX_PRODUCT_CHIPS);
  const overflow = products.length - shown.length;
  return (
    <>
      {shown.map((p) => (
        <span
          key={p}
          className="inline-block px-1.5 py-px rounded bg-primary-500/10 text-primary-700 text-[10px] font-medium max-w-[8rem] truncate flex-shrink-0"
          title={p}
        >
          {p}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="inline-block px-1.5 py-px rounded bg-gray-100 text-gray-500 text-[10px] flex-shrink-0"
          title={products.join(', ')}
        >
          +{overflow}
        </span>
      )}
    </>
  );
};

/**
 * Key cell: the key itself, then its route(s) and products on a quieter line.
 * Grouped BY product the chips would just repeat the key, so the line is routes
 * only.
 */
const KeyCell = ({
  group,
  showProducts = true,
}: {
  group: PeriodSummaryGroup;
  showProducts?: boolean;
}) => {
  const routes = group.routes || [];
  const routeTitle = routes.map(routeLabel).join('\n');
  return (
    <div className="min-w-0">
      <div className="font-medium text-slate-800 truncate" title={group.key}>
        {group.key}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
        {routes.length > 0 && (
          <span
            className={`text-[10px] truncate ${
              isGroupingRoute(routes[0]) ? 'text-gray-500' : 'text-gray-400 italic'
            }`}
            title={
              isGroupingRoute(routes[0]) ? routeTitle : `${routeTitle}\n(not on a route grouping)`
            }
          >
            {routeLabel(routes[0])}
            {routes.length > 1 && <span className="text-gray-400"> +{routes.length - 1}</span>}
          </span>
        )}
        {showProducts && <ProductChips products={group.products} />}
      </div>
    </div>
  );
};

export interface SummaryTableProps {
  groups: PeriodSummaryGroup[];
  totalGroups?: number | null;
  truncated?: boolean;
  groupBy: PeriodGroupBy;
  showBothSides?: boolean;
  sparklineSide?: PeriodSide;
  /** The period's full calendar (YYYY-MM-DD), for the Activity sparklines. */
  timelineDays?: string[];
  /** key → its sparkline; absent until the timeline call lands. */
  sparklines?: Map<string, PeriodSparklineEntry> | null;
  sort: PeriodSort;
  onSortChange: (columnId: string) => void;
  expandedKey: string | null;
  onToggleExpand: (key: string) => void;
  query: PeriodQueryParams;
  breakdowns: PeriodBreakdownCache;
  onLoadBreakdown: PeriodLoadBreakdown;
  onFilterRoute?: PeriodFilterRoute;
  searchTerm?: string;
}

const SummaryTable = ({
  groups,
  totalGroups,
  truncated,
  groupBy,
  showBothSides = true,
  sparklineSide = 'both',
  timelineDays = [],
  sparklines,
  sort,
  onSortChange,
  expandedKey,
  onToggleExpand,
  query,
  breakdowns,
  onLoadBreakdown,
  onFilterRoute,
  searchTerm,
}: SummaryTableProps) => {
  const keyLabel = groupByLabel(groupBy);
  const cols = columns(keyLabel, showBothSides);

  if (groups.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200 p-10 text-center">
        <FaInbox className="text-3xl text-gray-200 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
          {searchTerm
            ? `No ${keyLabel} matches “${searchTerm}”`
            : `No tickets with a ${keyLabel} in this period`}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {truncated && (
        <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-200">
          Showing {groups.length.toLocaleString()} of {Number(totalGroups || 0).toLocaleString()}{' '}
          groups — narrow the period or search to see the rest.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[1000px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50 text-left">
              <th scope="col" className="w-7 px-1.5 py-2.5 bg-gray-50">
                <span className="sr-only">Expand</span>
              </th>
              {cols.map((col) => {
                const active = sort.column === col.id;
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`px-2 py-2.5 font-medium text-gray-500 bg-gray-50 ${
                      col.wrap ? 'max-w-[4.5rem] leading-tight' : 'whitespace-nowrap'
                    } ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    {col.sortable === false ? (
                      <span>{col.label}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSortChange(col.id)}
                        aria-label={`Sort by ${col.label}`}
                        className={`inline-flex items-center gap-1 rounded hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-600/40 ${
                          active ? 'text-slate-800 font-semibold' : ''
                        }`}
                      >
                        {col.label}
                        {active &&
                          (sort.dir === 'asc' ? (
                            <FaSortAmountUp className="text-[8px]" />
                          ) : (
                            <FaSortAmountDown className="text-[8px]" />
                          ))}
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const expanded = expandedKey === g.key;
              const pct =
                g.variancePct !== null && g.variancePct !== undefined
                  ? g.variancePct
                  : variancePctOf(g.varianceTonnes, g.dispatchTonnes);
              const hauliers = g.hauliers || [];
              return (
                <React.Fragment key={g.key}>
                  <tr
                    data-testid={`period-summary-row-${g.key}`}
                    className={`${ROUTE_BOUNDARY_CLASS} transition-colors ${
                      expanded ? 'bg-primary-500/5' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-1.5 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => onToggleExpand(g.key)}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${g.key}`}
                        className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-slate-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-600/40"
                      >
                        {expanded ? (
                          <FaChevronDown className="text-[10px]" />
                        ) : (
                          <FaChevronRight className="text-[10px]" />
                        )}
                      </button>
                    </td>

                    <td className="px-2 py-2 max-w-[12rem]">
                      <KeyCell group={g} showProducts={groupBy !== 'product'} />
                    </td>

                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap align-top">
                      <span className="text-slate-800 font-semibold">{formatCount(g.tickets)}</span>
                      <div className="text-[10px] text-gray-400">
                        {formatCount(g.dispatchTickets)}d / {formatCount(g.receiptTickets)}r
                      </div>
                    </td>

                    <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-slate-800 align-top">
                      {formatTonnes(g.nettTonnes)}
                    </td>

                    {showBothSides && (
                      <>
                        <td className="px-2 py-2 text-right font-mono tabular-nums text-gray-500 align-top">
                          {formatTonnes(g.dispatchTonnes)}
                        </td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums text-gray-500 align-top">
                          {formatTonnes(g.receiptTonnes)}
                        </td>
                        <td
                          className={`px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap align-top ${varianceToneClass(pct)}`}
                        >
                          {g.varianceTonnes === null || g.varianceTonnes === undefined ? (
                            <span
                              className="text-gray-300"
                              title="Dispatch or receipt only — no variance"
                            >
                              one-sided
                            </span>
                          ) : (
                            <>
                              {formatTonnes(g.varianceTonnes)}
                              {pct !== null && pct !== undefined && (
                                <div className="text-[10px] opacity-70">{formatPct(pct)}</div>
                              )}
                            </>
                          )}
                        </td>
                      </>
                    )}

                    <td className="px-2 py-2 align-middle">
                      <Sparkline
                        days={timelineDays}
                        entry={sparklines?.get(g.key)}
                        side={sparklineSide}
                        keyLabel={g.key}
                      />
                    </td>

                    <td className="px-2 py-2 text-gray-500 whitespace-nowrap font-mono tabular-nums align-top">
                      <div className="text-slate-800">
                        {formatTimestampDayShort(g.firstTicket)} →{' '}
                        {formatTimestampDayShort(g.lastTicket)}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {formatCount(g.activeDays)} days
                      </div>
                    </td>

                    <td
                      className="px-2 py-2 text-right font-mono tabular-nums text-gray-500 whitespace-nowrap align-top"
                      title={
                        hauliers.length
                          ? `${hauliers.length} haulier(s):\n${hauliers.join('\n')}`
                          : 'No haulier recorded'
                      }
                    >
                      {formatCount(hauliers.length)}
                      <span className="text-gray-300"> · </span>
                      {formatCount(g.vehicleCount)}
                    </td>

                    <td className="px-2 py-2 text-right align-top">
                      {g.amendedTickets > 0 ? (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium"
                          title={`${g.amendedTickets} amended ticket(s)`}
                        >
                          {formatCount(g.amendedTickets)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={cols.length + 1} className="p-0">
                        <GroupDetail
                          groupKey={g.key}
                          groupBy={groupBy}
                          query={query}
                          breakdowns={breakdowns}
                          onLoadBreakdown={onLoadBreakdown}
                          onFilterRoute={onFilterRoute}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryTable;
